import type { Board, BoardData } from "@/model/board";
import { isPlanningBoard, isTeamBoard } from "@/model/board";
import type { Card } from "@/model/card";
import { isDependency, isRisk, isTeamLevelWorkitem } from "@/model/stickyType";
import { isGraphSignalsEnabled, isSignalsEnabled } from "@/utils/env/feature";

import { useBoardStore } from "../board";
import { riskyLinkStates } from "../helpers/linkState";
import { useIterationStore } from "../iteration";
import { useLinkStore } from "../link";
import { useTeamStore } from "../team";

/** A help paragraph as translation keys. A `{em}` placeholder in `text` is filled with `em` in italics. */
export interface HelpParagraph {
  text: string;
  em?: string;
  when?: () => boolean; // the paragraph is shown only while this is true
}

interface SignalDefinition {
  label: string; // translation key
  help?: HelpParagraph[];
  visible: (board: Board) => boolean;
}

interface ClientSignalDefinition extends SignalDefinition {
  matches: (card: Card, board: Board) => boolean;
  // the number shown in the menus; defaults to the number of matching cards
  count?: () => number;
}

export type Signal =
  | (ClientSignalDefinition & { id: ClientSignalId; source: "client" })
  | (SignalDefinition & { id: GraphSignalId; source: "graph" });

const onTeamBoard = (board: Board): board is BoardData<"team"> => isTeamBoard(board.type);
const onTeamOrPlanningBoard = (board: Board) => onTeamBoard(board) || isPlanningBoard(board.type);

/** Signals matched on the client. */
const clientSignals = {
  riskyLinks: {
    label: /*$t*/ "signals.riskyLinks",
    help: [
      { text: /*$t*/ "signals.riskyLinksHelp1" },
      { text: /*$t*/ "signals.riskyLinksHelp2", em: /*$t*/ "signals.riskyLinksHelp2Em" },
      { text: /*$t*/ "signals.riskyLinksHelp3", em: /*$t*/ "signals.riskyLinksHelp3Em" },
    ],
    visible: () => useBoardStore().showRiskyLinks,
    matches: hasRiskyLinkOnBoard,
    // counts links, not the cards at both ends
    count: () => {
      const { risky, critical } = useLinkStore().riskyLinkCount;
      return risky + critical;
    },
  },
  unassignedDependencies: {
    label: /*$t*/ "signals.unassignedDependencies",
    visible: onTeamOrPlanningBoard,
    matches: (card) => isDependency(card) && !card.dependTeam,
  },
  unaddressedRisks: {
    label: /*$t*/ "signals.unaddressedRisks",
    visible: (board) => onTeamOrPlanningBoard(board) && risksPossible(),
    matches: (card) => isRisk(card) && !card.risk,
  },
  unassignedOwnedRisks: {
    label: /*$t*/ "signals.unassignedOwnedRisks",
    visible: (board) => onTeamOrPlanningBoard(board) && risksPossible(),
    matches: (card) => isRisk(card) && card.risk === "O" && !card.assignee,
  },
  oversizedWorkItems: {
    label: /*$t*/ "signals.oversizedWorkItems",
    help: [
      { text: /*$t*/ "signals.oversizedWorkItemsHelp1" },
      { text: /*$t*/ "signals.oversizedWorkItemsHelp2" },
    ],
    visible: onTeamBoard,
    matches: isOversized,
  },
  unestimatedWorkItems: {
    label: /*$t*/ "signals.unestimatedWorkItems",
    help: [
      { text: /*$t*/ "signals.unestimatedWorkItemsHelp1" },
      { text: /*$t*/ "signals.unestimatedWorkItemsHelp2" },
    ],
    visible: onTeamBoard,
    matches: (card) => isTeamLevelWorkitem(card) && card.points === 0,
  },
} satisfies Record<string, ClientSignalDefinition>;

/** Signals whose matching card ids come from the server's graph. */
const graphSignals = {
  unplannedDependencies: {
    label: /*$t*/ "signals.unplannedDependencies",
    help: [
      { text: /*$t*/ "signals.unplannedDependenciesHelp1" },
      { text: /*$t*/ "signals.unplannedDependenciesHelpExternal", when: hasExternalTeams },
    ],
    visible: onTeamOrPlanningBoard,
  },
  noUpperLevelLink: {
    label: /*$t*/ "signals.noUpperLevelLink",
    help: [
      { text: /*$t*/ "signals.noUpperLevelLinkHelp1" },
      { text: /*$t*/ "signals.noUpperLevelLinkHelp2" },
    ],
    visible: onTeamBoard,
  },
  noObjective: {
    label: /*$t*/ "signals.noObjective",
    help: [
      { text: /*$t*/ "signals.noObjectiveHelp1" },
      { text: /*$t*/ "signals.noObjectiveHelp2" },
    ],
    visible: onTeamBoard,
  },
  notOnArtBoard: {
    label: /*$t*/ "signals.notOnArtBoard",
    help: [{ text: /*$t*/ "signals.notOnArtBoardHelp1" }],
    visible: onTeamBoard,
  },
} satisfies Record<string, SignalDefinition>;

type ClientSignalId = keyof typeof clientSignals;
export type GraphSignalId = keyof typeof graphSignals;
export type SignalId = ClientSignalId | GraphSignalId;

/** Signal groups in display order, each with an optional subheader translation key. */
export const signalGroups: ReadonlyArray<{ subheader?: string; ids: readonly SignalId[] }> = [
  { ids: ["riskyLinks", "unplannedDependencies", "unaddressedRisks", "unassignedDependencies"] },
  {
    subheader: /*$t*/ "signals.group.potentialRisk",
    ids: [
      "oversizedWorkItems",
      "unestimatedWorkItems",
      "unassignedOwnedRisks",
      "noUpperLevelLink",
      "noObjective",
      "notOnArtBoard",
    ],
  },
];

export const signalIds: SignalId[] = signalGroups.flatMap((group) => group.ids);

/** The signals that apply to the board, none without the signals flag; graph signals need the graph flags. */
export function boardSignals(board: Board): Signal[] {
  if (!isSignalsEnabled()) return [];
  const graphEnabled = isGraphSignalsEnabled();
  return signalIds
    .map(toSignal)
    .filter((signal) => signal.visible(board) && (signal.source === "client" || graphEnabled));
}

function toSignal(id: SignalId): Signal {
  return isClientSignalId(id)
    ? { id, source: "client", ...clientSignals[id] }
    : { id, source: "graph", ...graphSignals[id] };
}

function isClientSignalId(id: SignalId): id is ClientSignalId {
  return id in clientSignals;
}

/** A risky or critical link whose other end is on the board. */
function hasRiskyLinkOnBoard(card: Card, board: Board): boolean {
  return card.linkedStickies.some(
    (link) =>
      riskyLinkStates.has(useLinkStore().cardLinkState(card, link)) &&
      !!useLinkStore().boardCardByLink(link.stickyId, board),
  );
}

/** A risk sticky type can be on the current board. */
function risksPossible(): boolean {
  return useBoardStore().possibleStickyTypes.some((type) => type.functionality === "risk");
}

/** The current ART has external teams. */
function hasExternalTeams(): boolean {
  return useTeamStore().externalsInCurrentArt.length > 0;
}

function isOversized(card: Card, board: Board): boolean {
  if (!onTeamBoard(board) || !isTeamLevelWorkitem(card) || card.iterationId === null) {
    return false;
  }
  const velocity = useIterationStore().iteration(board, card.iterationId).velocity;
  return velocity > 0 && card.points > velocity / 2;
}
